import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { AppRemixIcon, AppCard } from '@/layout/AppComponents';
import axios from 'axios';
import ReactDOMServer from 'react-dom/server';
import * as moduleAction from '../../../store/actions/moduleAction';
import * as currentPageAction from '../../../store/actions/currentPageAction';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import { axiosPath, path } from '../../../routing/helpers/router';
import NoInfo from '../../../layout/components/helpers/NoInfo';
import colors from '../../../constants/colors';
import SpinnerLoader from '../../../layout/components/helpers/SpinnerLoader';
import UserManagementModuleSidebar from '../../../layout/screenSpecifics/Modules/UserManagement/UserManagementModuleSidebar';
import Translator from '../../../layout/components/helpers/Translator';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import UserManagementPaginator from '../../../layout/screenSpecifics/Modules/UserManagement/UserManagementPaginator';
import errorHandler from '@/layout/components/error/ErrorHandler';
import UserManagementBase from './UserManagementBase';

export default function UserManagementHomeScreen() {
  const { moduleBridgeHex, page } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const permissions = useSelector(
    (state) => state.module[moduleBridgeHex]?.permissions
  );
  const isSidebarOpened = useSelector(
    (state) => state.auth?.settings?.preferences?.sidebar?.isOpened
  );
  const users = useSelector((state) => state.currentPage?.users);
  const pagination = useSelector((state) => state.currentPage?.pagination);
  const userLevel1 = useSelector(
    (state) => state.currentPage?.userLevels?.level1
  );
  const [moduleBridge, setModuleBridge] = useState();
  const [user, setUser] = useState();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    dispatch(currentPageAction.change('userLevels', {}, true));

    return () => {
      dispatch(currentPageAction.empty());
    };
  }, []);

  useEffect(() => {
    if (users && users?.[page - 1] === undefined) {
      navigate(
        path({
          name: 'user_module_userManagement_home',
          params: {
            moduleBridgeHex,
            page: 1,
          },
        })
      );
    }
  }, [users]);

  const handleLoadUserIDs = useCallback(async () => {
    await axios
      .post(
        axiosPath('api_r_user_module_userManagement_user_list_pagination'),
        {
          moduleBridgeHex,
        }
      )
      .then(({ data }) => {
        if (isMounted.current) {
          setModuleBridge(data.moduleBridge);
          setUser(data.user);

          dispatch(currentPageAction.change('users', data.userIDs));
          dispatch(currentPageAction.change('pagination', data.pagination));
          dispatch(currentPageAction.change('usersPage', data.userIDs));
        }
      })
      .catch((e) => dispatch(errorHandler(e)));
  }, []);

  useEffect(() => {
    handleLoadUserIDs();
  }, []);

  // > LOAD USER LEVELS
  const loadUserLevel = async (level) => {
    try {
      const response = await axios.post(
        axiosPath('api_r_user_module_userManagement_loadUserLevel'),
        {
          moduleBridgeHex,
          userIDs: users?.[page - 1],
          level,
        }
      );
      return response.data;
    } catch (err) {}
  };

  // Level 1
  useEffect(() => {
    if (users) {
      window.scrollTo(0, 0);
      if (users.length > 0) {
        setLoading(true);
        loadUserLevel(1).then((userLvl1) => {
          dispatch(
            currentPageAction.change(
              'userLevels',
              {
                level1: !userLevel1 ? userLvl1 : { ...userLevel1, ...userLvl1 },
              },
              true
            )
          );
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    }
  }, [users, page]);
  // < LOAD USER LEVELS

  const handleSelectedUser = useCallback((userName) => {
    navigate(
      path({
        name: 'user_module_userManagement_userInfo',
        params: {
          moduleBridgeHex,
          userName,
        },
      })
    );
  }, []);

  const handleEditUser = useCallback((userName) => {
    navigate(
      path({
        name: 'user_module_userManagement_edit',
        params: {
          moduleBridgeHex,
          userName,
        },
      })
    );
  }, []);

  return (
    <div>
      <BreadcrumbHandler
        paths={
          moduleBridge
            ? [
                {
                  name: moduleBridge?.name,
                },
              ]
            : [{ name: '...' }]
        }
      />
      <UserManagementBase />
      <UserManagementModuleSidebar permissionLevel={user?.permissionLevel} />
      <div className="row">
        <main role="main" className="col-md-12 col-lg-12 px-md-4">
          <div className={`${isSidebarOpened && 'sidebar-expanded'}`}>
            <div className="justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-0 mb-0 border-bottom">
              <AppCard
                header={
                  <span>
                    <Translator
                      file="module-userManagement"
                      name="module.um.user-list"
                    />{' '}
                    {user?.permissionLevel === 1 ? (
                      <span>
                        (
                        <Translator
                          file="module-userManagement"
                          name="module.um.only-users-created-by"
                        />{' '}
                        {user?.user})
                      </span>
                    ) : (
                      ''
                    )}
                  </span>
                }
              >
                {!loading ? (
                  <>
                    {users?.length > 0 && userLevel1 !== undefined ? (
                      <div className="table-responsive">
                        <table className="table table-hover">
                          <thead>
                            <tr>
                              <th>
                                <Translator
                                  file="module-userManagement"
                                  name="module.um.user"
                                />
                              </th>
                              <th>
                                <Translator
                                  file="module-userManagement"
                                  name="module.um.name"
                                />
                              </th>
                              <th>
                                <Translator
                                  file="module-userManagement"
                                  name="module.um.email"
                                />
                              </th>
                              <th>
                                <Translator
                                  file="module-userManagement"
                                  name="module.um.date-creation"
                                />
                              </th>
                              {user.permissionLevel < 2 && (
                                <th>
                                  <Translator
                                    file="module-userManagement"
                                    name="module.um.options"
                                  />
                                </th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {users?.[page - 1].map((id) => (
                              <tr
                                key={id}
                                style={{
                                  backgroundColor:
                                    userLevel1[id]?.isCreaterUser &&
                                    user?.permissionLevel === 0
                                      ? colors.info
                                      : '',
                                }}
                                title={`${
                                  userLevel1[id]?.isCreaterUser &&
                                  user?.permissionLevel === 0
                                    ? ReactDOMServer.renderToString(
                                        <Translator
                                          file="module-userManagement"
                                          name="module.um.user-was-created-by-you"
                                        />
                                      )
                                    : ''
                                }`}
                              >
                                <td className="align-middle">
                                  <span
                                    className="pointer text-primary"
                                    onClick={() =>
                                      handleSelectedUser(userLevel1[id]?.user)
                                    }
                                  >
                                    {userLevel1[id]?.user}
                                  </span>
                                </td>
                                <td className="align-middle">
                                  {userLevel1[id]?.name}
                                </td>
                                <td className="align-middle">
                                  {userLevel1[id]?.email}
                                </td>
                                <td className="align-middle">
                                  {userLevel1[id]?.dateStamp}
                                </td>
                                {user.permissionLevel < 2 && (
                                  <td>
                                    <AppRemixIcon
                                      icon="pencil"
                                      color={colors.primary}
                                      title={ReactDOMServer.renderToString(
                                        <Translator file="general" name="general.edit" />
                                      )}
                                      size={22}
                                      onClick={() =>
                                        handleEditUser(userLevel1[id]?.user)
                                      }
                                      className="pointer"
                                    />
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div>
                        <NoInfo
                          icon={
                            <AppRemixIcon
                              icon="team"
                              size={30}
                              color={colors.primaryLight}
                            />
                          }
                          text={ReactDOMServer.renderToString(
                            <Translator
                              file="module-userManagement"
                              name="module.um.client-no-registered-users"
                            />
                          )}
                          color={colors.primaryLight}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <NoInfo
                    icon={<SpinnerLoader />}
                    text={ReactDOMServer.renderToString(
                      <Translator
                        file="module-userManagement"
                        name="module.um.loading"
                      />
                    )}
                    color={colors.primary}
                  />
                )}
              </AppCard>
              {permissions?.userManagement?.paginator &&
                users?.length > 0 &&
                users?.[page - 1] !== undefined && (
                  <UserManagementPaginator
                    pagination={pagination}
                    route="user_module_userManagement_home"
                    page={page}
                    plantObligationPages={users}
                    itemsPerPage={100}
                    className="mt-3"
                    params={{
                      moduleBridgeHex,
                      page,
                    }}
                  />
                )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
